/**
 * Identity gating for consultation review CTAs (ENG-5259).
 *
 * A review prompt must only appear for a participant acting in their reviewer
 * role: the requester (who reviews the expert) or the expert (who reviews the
 * client). Account managers, team members and staff viewing someone else's
 * consultation are none of these and must never see a review CTA.
 *
 * Mirrors the consultation detail page's existing `isViewerClient ||
 * isViewerExpert` gate (Consultation.tsx) so every surface agrees on who may
 * review.
 *
 * Callers must select `requester { id }` and `expert { id }` on the consultation.
 * A missing id fails closed (returns false, hiding the CTA) — the gate never
 * over-shows; the cost is that an under-selected query silently hides the CTA
 * from a legitimate reviewer.
 */

interface ReviewParticipants {
  requester?: { id?: string | null } | null;
  expert?: { id?: string | null } | null;
}

export function isConsultationRequester(
  viewerId: string | null | undefined,
  consultation: ReviewParticipants | null | undefined
): boolean {
  return !!viewerId && consultation?.requester?.id === viewerId;
}

export function isConsultationExpert(
  viewerId: string | null | undefined,
  consultation: ReviewParticipants | null | undefined
): boolean {
  return !!viewerId && consultation?.expert?.id === viewerId;
}

/** True when the viewer may review this consultation — its requester or expert. */
export function canReviewConsultation(
  viewerId: string | null | undefined,
  consultation: ReviewParticipants | null | undefined
): boolean {
  return (
    isConsultationRequester(viewerId, consultation) || isConsultationExpert(viewerId, consultation)
  );
}
